import {gql} from '@apollo/client';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';
import isEqual from 'react-fast-compare';

import getArchiveAwareDisplayName from 'archive/getArchiveAwareDisplayName';
import {InsightsFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {isValidationRulesSupportedType} from 'constants/RegistryConstants';
import {BatchLinkProps} from 'constants/SampleConstants';
import {TagSchemaItemType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {assertIsAccessible, assertIsAccessibleApiId} from 'graphql-client/InaccessibleInfo';
import LookupConfigModal from 'lookup-config/LookupConfigModal';
import {LookupConfigSpec} from 'lookup-config/LookupConfigModalConstants';
import FieldActionButtons, {ExtraActionButtons} from 'metadata/FieldActionButtons';
import {count} from 'metrics/Metrics';
import notifications from 'notifications';
import {ComputedFieldQuery, ComputedFieldQueryVariables} from 'registry/field/__generated/ComputedFieldQuery';
import FieldDefinitionInputOrDisplay from 'registry/field/FieldDefinitionInputOrDisplay';
import {FieldNameRenderingProps} from 'registry/field/FieldTable';
import {getFieldDefinition} from 'registry/field/FieldUtil';
import SimpleDivTableLastCell from 'registry/field/SimpleDivTableLastCell';
import {AdvancedSettingsIconProps} from 'schemas/fields/AdvancedSettingsIconUtils';
import {
  FieldMigrationCreationModal,
  FieldMigrationSettings,
} from 'schemas/fields/field-migration/FieldMigrationCreationModal';
import FieldChangeWarningModal, {
  FieldChangeWarehouseWarning,
  FieldRelaxationWarning,
  shouldShowFieldChangeWarehouseWarning,
} from 'schemas/fields/FieldChangeWarningModal';
import isSchemaFieldArchived, {getSchemaFieldArchivableItem} from 'schemas/fields/isSchemaFieldArchived';
import * as SchemaFieldConstants from 'schemas/fields/SchemaFieldConstants';
import {
  ANY_ENTITY,
  ComputedFieldType,
  FieldDefinition,
  isRequiredLinkFolderItemType,
  isRequiredLinkSchemaInterface,
  isRequiredLinkTagSchema,
  SchemaField,
  UNIT_AWARE_FIELD_DEFINITIONS,
} from 'schemas/fields/SchemaFieldConstants';
import SchemaFieldName from 'schemas/fields/SchemaFieldName';
import SchemaFieldTooltipTextField, {getTooltipTextError} from 'schemas/fields/SchemaFieldTooltipTextField';
import * as SchemaFieldUtil from 'schemas/fields/SchemaFieldUtil';
import SchemaFieldWarehouseNameField from 'schemas/fields/SchemaFieldWarehouseNameField';
import {UneditableFieldIcon} from 'schemas/fields/UneditableFields';
import * as SchemaFieldSelectorConstants from 'schemas/SchemaFieldSelectorConstants';
import {SnapshotFieldLookupConfigModalProps} from 'schemas/SnapshotFieldLookupConfigUtil';
import * as TagSchemaUtil from 'tag-schema/util/TagSchemaUtil';
import getTestProps from 'test/lib/cypress/getTestProps';
import Accordion from 'ui-library/Accordion';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import FormInput from 'ui-library/bs/FormInput';
import {TooltipWrapper} from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import * as Colors from 'ui-library/Colors';
import {GRAY_DARKER, GRAY_LIGHTER} from 'ui-library/Colors';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import legacyRunModal from 'ui-library/legacyRunModal';
import copyToClipboard from 'util/clipboard/copyToClipboard';
import * as keyboard from 'util/Keyboard';
import * as modals from 'util/Modals';
import {typeToName} from 'util/NameUtil';
import {ElementKeyboardEvent, InputChangeEvent} from 'util/Types';
import {SimpleRule} from 'validation_rules/components/simple_ui/types';
import {validationRuleTemplateIdToDisplayName} from 'validation_rules/components/simple_ui/utils';
import {ValidationRule} from 'validation_rules/types';
import {isSimpleRulesEnabled} from 'validation_rules/utils';

import type {FieldTableQuery} from './__generated/FieldTableQuery';

type FieldTableQuerySelector = FieldTableQuery['schemaFieldSelectors'][number];

export const dependencyStringCharacterLimit = 500;
export const dependencyCountLimit = 20;
const ARCHIVE_SNAPSHOT_FIELD_WARNING =
  'After archiving, this snapshot field will become a regular field. Existing values will be preserved. Unarchiving the field will not restore it back into a snapshot field.';
interface FieldTableRowUnitData {
  unitApiIdentifier?: string | null;
  unitSymbol?: string | null;
}

interface FieldTableRowSimpleRulesData {
  simpleRules?: Array<SimpleRule>;
}

export interface FieldTableRowState {
  isInEditMode: boolean; // Whether the row should show form inputs
  // TODO(T6639): Fix reverting edit values when initialField is reverted to original value, and
  // maintaining edit values if row is moved while being edited
  editingName: string;
  editingSystemName: string;
  editingIsMulti: boolean;
  editingIsParentLink: boolean;
  editingIsRequired: boolean;
  editingIsIntegration: boolean;
  editingLookupConfig?: LookupConfigSpec;
  editingTooltipText?: string | null;
  // The fieldOption indicating the currently-selected field definition (if any).
  fieldOption: SchemaFieldConstants.LeafFieldOption | null;
  editingUnitData: FieldTableRowUnitData;
  editingSimpleRules: FieldTableRowSimpleRulesData;
  editingDecimalPrecision?: number | null;
}

export enum DisablableControl {
  ARCHIVE_BUTTON = 'ARCHIVE_BUTTON',
  DELETE_BUTTON = 'DELETE_BUTTON',
  DISPLAY_NAME_INPUT = 'DISPLAY_NAME_INPUT',
  FIELD_DEFINITION_DROPDOWN = 'FIELD_DEFINITION_DROPDOWN',
  IS_MULTI_CHECKBOX = 'IS_MULTI_CHECKBOX',
  IS_REQUIRED_CHECKBOX = 'IS_REQUIRED_CHECKBOX',
  WAREHOUSE_NAME_INPUT = 'WAREHOUSE_NAME_INPUT',
}

export interface FieldMigrationReviewProps {
  isReviewableForFieldMigration: boolean;
  reviewMigration: () => void;
}

export interface FieldTableRowProps {
  areRemoveAndArchiveEnabled: boolean;
  availableFieldOptions: Array<SchemaFieldConstants.FieldOption>;
  batchLinkProps?: BatchLinkProps;
  disableControlsWithTooltips?: {[key in DisablableControl]?: string | null};
  disableDrag: boolean;
  extraActionButtons?: ExtraActionButtons;
  id: string;
  initialField: SchemaFieldConstants.SchemaField; // TODO(BNCH-30720) Mismatches ItemProps in FieldTable.tsx
  isInEditMode: boolean;
  isDragging: boolean;
  isFieldBeingCreated: boolean;
  isRemoveOnly: boolean;
  isSchemaOrInterfaceBeingCreated: boolean;
  onCancel: (id: string) => void;
  onDragStart?: () => void;
  onEdit: (id: string) => void;
  refetchSchemaAfterUpdate?: () => Promise<void>;
  requestArchive?: (id: string) => void;
  requestChange: (id: string, fieldWithChanges: SchemaFieldConstants.SchemaField) => void;
  requestRemove: (id: string) => void;
  requestFieldMigrationCreation?: (
    id: string,
    field: SchemaFieldConstants.SchemaField,
    locale?: string
  ) => Promise<void>;
  requestUnarchive?: (id: string) => void;
  disabled: boolean;
  schemaFieldSelectors:
    | Array<FieldTableQuerySelector>
    | Array<SchemaFieldSelectorConstants.SchemaFieldSelector>;
  shouldShowIsParentLink: boolean;
  // If provided, will instantiate this component as an extra action button during field creation
  advancedSettingsComponentForFieldCreation?: React.ComponentType<AdvancedSettingsIconProps>;
  shouldShowIsRequired: boolean;
  shouldShowMultiSelect?: boolean;
  shouldShowValidations?: boolean;
  shouldShowIsIntegration: boolean;
  shouldShowWarehouseName?: boolean;
  shouldShowRelaxationWarning: boolean;
  shouldShowWarehouseWarning: boolean;
  ownerUrlId: string;
  registryId?: string | null;
  lookupConfigModalProps?: SnapshotFieldLookupConfigModalProps;
  isUneditable?: boolean; // TODO(BNCH-56776): Make required
  submissionWarnings: Array<JSX.Element>;
  /** set to true if it should display the submission warnings when creating a new field */
  shouldShowSubmissionWarningOnCreate?: boolean;
  migratingFromFieldName?: string | null;
  migratingToFieldName?: string | null;
  isFieldMigrationAvailable: boolean;
  shouldShowTooltipText?: boolean;
  isReviewableForFieldDataMigration?: boolean;
  reviewMigration?: () => void;
  schemaNumItems?: number;
  fieldMigrationReviewProps?: FieldMigrationReviewProps;
  schemaIsMixtureSchema?: boolean;
  // schemaId used for tag schemas to query computed field configuration; might be set to null if schemaId is not easily accessible in other cases
  schemaId: string | null;
  itemType?: TagSchemaItemType | null;
  validationRules?: Array<ValidationRule>;
  fieldNameRenderingProps?: FieldNameRenderingProps;
  compactMode?: boolean;
}

export const COMPUTED_FIELD_QUERY = gql`
  query ComputedFieldQuery($schemaApiId: StrictString!, $fieldApiId: StrictString!)
  @skipAudit
  @engComponent(name: SCHEMAS) {
    tagSchema(id: $schemaApiId) {
      ... on TagSchemaNode {
        id
        schemaField(id: $fieldApiId) {
          ... on SchemaFieldNode {
            id
            apiId
            name
            formulaDescription
            dependencyStrings
          }
          ...InaccessibleInfoGraphQL
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export interface QueryingProps {
  schemaApiId: string;
  fieldApiId: string;
}
export function QueryingComputedFieldInfo({schemaApiId, fieldApiId}: QueryingProps): JSX.Element | null {
  const result = useBenchlingQuery<ComputedFieldQuery, ComputedFieldQueryVariables>(COMPUTED_FIELD_QUERY, {
    variables: {schemaApiId, fieldApiId},
  });

  if (result.type === QueryResultType.ERROR) {
    return null;
  } else if (result.type === QueryResultType.PENDING) {
    return <CenteredHorizontalThrobber />;
  } else {
    const tagSchema = assertIsAccessible(result.data.tagSchema);
    const schemaField = assertIsAccessibleApiId(tagSchema.schemaField);
    const dependencyStrings = schemaField.dependencyStrings;
    const dependencyStringsDisplay: Array<string> = [];

    dependencyStrings.forEach((dependency: string, index: number) => {
      if (index < dependencyCountLimit) {
        if (dependency.length > dependencyStringCharacterLimit) {
          dependencyStringsDisplay.push(`${dependency.slice(0, dependencyStringCharacterLimit)}...`);
        } else {
          dependencyStringsDisplay.push(dependency);
        }
      }
    });

    if (dependencyStrings.length > dependencyCountLimit) {
      dependencyStringsDisplay.push('...');
    }

    return (
      <div {...getTestProps({component: 'QueryingComputedFieldInfo', element: 'root'})}>
        <ul className={css(styles.customList)}>
          <li className={`${css(styles.listItem)} formula-description`}>
            Formula Description: {schemaField.formulaDescription}
          </li>
          <li className={`${css(styles.listItem)} formula-dependencies`}>
            Formula Dependencies:
            <ul className={`${css(styles.customList)} dependency-list`}>
              {dependencyStringsDisplay.map((dependency, index) => (
                <li key={index} className={`${css(styles.dependencyItem)} dependency-item`}>
                  {dependency}
                </li>
              ))}
            </ul>
          </li>
        </ul>
      </div>
    );
  }
}

// Keep in sync with:
// - benchling/registry/child_entities/is_parent_link.py::_can_field_be_parent_field()
// - benchling/schemas/tag_schemas/models/child_entity_link_config.py::validate_child_schema_link_field()
const SUPPORTED_PARENT_LINK_FIELD_TYPES: Array<SchemaFieldConstants.FieldType> = [
  SchemaFieldConstants.FieldType.BIOENTITY_LINK,
  SchemaFieldConstants.FieldType.BASE_MOLECULE_LINK,
];

export function getParentLinkDisabledReason(
  field: SchemaField,
  schemaIsMixtureSchema?: boolean
): string | null {
  if (!SUPPORTED_PARENT_LINK_FIELD_TYPES.includes(field.fieldType)) {
    return 'Parent link is only available for entity link fields';
  }
  const tagSchema = SchemaFieldConstants.getRequiredLinkTagSchema(field);
  if (field.isMulti && tagSchema?.folderItemType === ModelTypeConstants.MIXTURE && schemaIsMixtureSchema) {
    return 'Multi-value mixture-to-mixture links cannot be parent links';
  }
  if (tagSchema == null) {
    const customInterface = SchemaFieldConstants.getRequiredLinkCustomSchemaInterface(field);
    if (customInterface == null) {
      return 'Select a specific entity schema to enable parent link';
    }
  }
  return null;
}

export function isMultiSelectDisabled(
  field: SchemaField,
  schemaIsMixtureSchema?: boolean,
  disableControlsWithTooltips?: {[key in DisablableControl]?: string | null}
): boolean {
  return (
    !TagSchemaUtil.isLinkField(field) ||
    // Disallow making a parent link isMulti if is a mixture->mixture link
    (field.isParentLink &&
      SchemaFieldConstants.getRequiredLinkTagSchema(field)?.folderItemType === ModelTypeConstants.MIXTURE &&
      schemaIsMixtureSchema) ||
    disableControlsWithTooltips?.[DisablableControl.IS_MULTI_CHECKBOX] != null
  );
}

class FieldTableRow extends React.Component<FieldTableRowProps> {
  static defaultProps = {
    isInEditMode: false,
    isDragging: false,
    disableDrag: false,
    isFieldBeingCreated: false,
    areRemoveAndArchiveEnabled: true,
    isRemoveOnly: false,
    isSchemaOrInterfaceBeingCreated: false,
    disabled: false,
    shouldShowIsParentLink: false,
    shouldShowIsRequired: false,
    shouldShowMultiSelect: true,
    shouldShowValidations: false,
    shouldShowIsIntegration: false,
    shouldShowWarehouseName: true,
    shouldShowRelaxationWarning: false,
    shouldShowWarehouseWarning: false,
    submissionWarnings: [],
    shouldShowSubmissionWarningOnCreate: false,
    isFieldMigrationAvailable: false,
    shouldShowTooltipText: false,
    isReviewableForFieldDataMigration: false,
  };

  override state: FieldTableRowState = {
    isInEditMode: this.props.isInEditMode,
    // TODO(T6639): Fix reverting edit values when initialField is reverted to original value, and
    // maintaining edit values if row is moved while being edited
    editingName: this.props.initialField.displayName || this.props.initialField.name,
    editingSystemName: this.props.initialField.systemName,
    editingIsMulti: this.props.initialField.isMulti,
    editingIsParentLink: this.props.initialField.isParentLink || false,
    editingDecimalPrecision: this.props.initialField.decimalPrecision,
    editingIsRequired: this.props.initialField.isRequired,
    editingIsIntegration: this.props.initialField.isIntegration,
    editingLookupConfig: this.props.initialField.lookupConfig,
    editingTooltipText: this.props.initialField.tooltipText,
    // The fieldOption indicating the currently-selected field definition (if any).
    fieldOption: null,
    editingUnitData: {
      unitApiIdentifier: this.props.initialField.unitApiIdentifier,
      unitSymbol: this.props.initialField.unitSymbol,
    },
    editingSimpleRules: {
      simpleRules: this.props.initialField.simpleRules,
    },
  };

  static getDerivedStateFromProps(
    nextProps: FieldTableRowProps,
    prevState: FieldTableRowState
  ): Partial<FieldTableRowState> | null {
    if (!prevState.isInEditMode || nextProps.isSchemaOrInterfaceBeingCreated) {
      const updatedState = {
        ...(prevState.editingSystemName !== nextProps.initialField.systemName && {
          editingSystemName: nextProps.initialField.systemName,
        }),
        ...(prevState.editingName !== (nextProps.initialField.displayName || nextProps.initialField.name) && {
          editingName: nextProps.initialField.displayName || nextProps.initialField.name,
        }),
        ...(prevState.editingIsMulti !== nextProps.initialField.isMulti && {
          editingIsMulti: nextProps.initialField.isMulti,
        }),
        ...(prevState.editingIsParentLink !== nextProps.initialField.isParentLink && {
          editingIsParentLink: nextProps.initialField.isParentLink || false,
        }),
        ...(prevState.editingIsRequired !== nextProps.initialField.isRequired && {
          editingIsRequired: nextProps.initialField.isRequired,
        }),
        ...(prevState.editingIsIntegration !== nextProps.initialField.isIntegration && {
          editingIsIntegration: nextProps.initialField.isIntegration,
        }),
        ...(prevState.editingTooltipText !== nextProps.initialField.tooltipText && {
          editingTooltipText: nextProps.initialField.tooltipText,
        }),
        ...(prevState.editingDecimalPrecision !== nextProps.initialField.decimalPrecision && {
          editingDecimalPrecision: nextProps.initialField.decimalPrecision,
        }),
        ...(!isEqual(prevState.editingLookupConfig, nextProps.initialField.lookupConfig) && {
          editingLookupConfig: nextProps.initialField.lookupConfig,
        }),
        ...((prevState.editingUnitData?.unitApiIdentifier !== nextProps.initialField.unitApiIdentifier ||
          prevState.editingUnitData?.unitSymbol !== nextProps.initialField.unitSymbol) && {
          editingUnitData: {
            unitApiIdentifier: nextProps.initialField.unitApiIdentifier,
            unitSymbol: nextProps.initialField.unitSymbol,
          },
        }),
        ...(prevState.fieldOption && {
          fieldOption: {
            ...prevState.fieldOption,
            ...(prevState.fieldOption.fieldDefinition !== nextProps.initialField.fieldType && {
              fieldDefinition: nextProps.initialField.fieldType,
            }),
            ...(prevState.fieldOption.schemaFieldSelectorId !==
              nextProps.initialField.schemaFieldSelectorId && {
              schemaFieldSelectorId: nextProps.initialField.schemaFieldSelectorId,
            }),
            ...(prevState.fieldOption.requiredLink !== nextProps.initialField.requiredLink && {
              requiredLink: nextProps.initialField.requiredLink,
            }),
          },
        }),
        ...(!isEqual(prevState.editingSimpleRules?.simpleRules, nextProps.initialField.simpleRules) && {
          editingSimpleRules: {
            simpleRules: nextProps.initialField.simpleRules,
          },
        }),
      };
      return updatedState;
    }
    return null;
  }

  private setNewField(
    fieldOption: SchemaFieldConstants.LeafFieldOption,
    lookupConfig?: LookupConfigSpec
  ): void {
    const isSnapshot = fieldOption.fieldDefinition === ComputedFieldType.SNAPSHOT;
    this.setStateAndMaybeUpdateParent({
      fieldOption,
      // Force isMulti to false if non-link field since isMulti is currently only supported for links.
      editingIsMulti:
        !isSnapshot && TagSchemaUtil.isLinkFieldOption(fieldOption) ? this.state.editingIsMulti : false,
      editingIsRequired: isSnapshot ? false : this.state.editingIsRequired,
      editingIsIntegration: isSnapshot ? false : this.state.editingIsIntegration,
      editingIsParentLink: isSnapshot ? false : this.state.editingIsParentLink,
      editingLookupConfig: isSnapshot ? (lookupConfig ?? this.state.editingLookupConfig) : undefined,
    });
  }

  // If the user is creating a new field and the field definition is not numeric, make sure no unit is stored.
  private maybeClearOutFieldUnit(fieldOption: SchemaFieldConstants.LeafFieldOption): void {
    if (
      this.state.editingUnitData.unitApiIdentifier != null &&
      this.props.isFieldBeingCreated &&
      !UNIT_AWARE_FIELD_DEFINITIONS.includes(fieldOption.fieldDefinition as SchemaFieldConstants.FieldType)
    ) {
      this.setStateAndMaybeUpdateParent({editingUnitData: {unitApiIdentifier: null, unitSymbol: null}});
    }
  }

  private clearOutSimpleRules(): void {
    if ((this.state.editingSimpleRules.simpleRules ?? []).length > 0) {
      this.setStateAndMaybeUpdateParent({editingSimpleRules: {simpleRules: []}});
    }
  }

  private handleChangeFieldOption(fieldOption: SchemaFieldConstants.LeafFieldOption): void {
    if (fieldOption.fieldDefinition === ComputedFieldType.SNAPSHOT && this.props.lookupConfigModalProps) {
      legacyRunModal(LookupConfigModal, {
        ...this.props.lookupConfigModalProps,
        initialLookupConfig: this.state.editingLookupConfig,
        onRequestClose: () => {},
        onRequestSubmit: ([{lookupConfig}]) => {
          this.maybeClearOutFieldUnit(fieldOption);
          this.setNewField(fieldOption, lookupConfig);
          if (isSimpleRulesEnabled()) {
            this.clearOutSimpleRules();
          }
        },
      });
    } else {
      this.maybeClearOutFieldUnit(fieldOption);
      this.setNewField(fieldOption);
      if (isSimpleRulesEnabled()) {
        this.clearOutSimpleRules();
      }
    }
  }

  private handleChangeName = (e: InputChangeEvent): void => {
    this.setStateAndMaybeUpdateParent({editingName: e.target.value});
  };

  private handleChangeSystemName = (e: InputChangeEvent): void => {
    this.setStateAndMaybeUpdateParent({editingSystemName: e.target.value});
  };

  private handleChangeIsRequired = ({
    target: {checked: newIsRequired},
  }: {
    target: {checked: boolean};
  }): void => {
    this.setStateAndMaybeUpdateParent({editingIsRequired: newIsRequired});
  };

  private handleChangeIsIntegration = ({
    target: {checked: newIsIntegration},
  }: {
    target: {checked: boolean};
  }): void => {
    this.setStateAndMaybeUpdateParent({editingIsIntegration: newIsIntegration});
  };

  private handleChangeIsMulti = ({target: {checked: newIsMulti}}: {target: {checked: boolean}}): void => {
    this.setStateAndMaybeUpdateParent({editingIsMulti: newIsMulti});
  };

  private handleChangeIsParentLink = ({
    target: {checked: newIsParentLink},
  }: {
    target: {checked: boolean};
  }): void => {
    this.setStateAndMaybeUpdateParent({editingIsParentLink: newIsParentLink});
  };

  private handleChangeTooltipText = (e: InputChangeEvent): void => {
    this.setStateAndMaybeUpdateParent({editingTooltipText: e.target.value || null});
  };

  /**
   * This component is used as a controlled component when isSchemaOrInterfaceBeingCreated is true and an
   * uncontrolled component when isSchemaOrInterfaceBeingCreated is false, so we need to implement both state
   * update strategies based on which case we are.
   */
  private setStateAndMaybeUpdateParent(stateChange: Partial<FieldTableRowState>): void {
    this.setState(stateChange);
    if (this.props.isSchemaOrInterfaceBeingCreated) {
      const newState = {...this.state, ...stateChange};
      this.props.requestChange(this.props.id, this.getFieldWithChanges(newState));
    }
  }

  /**
   * Resolves this.props.initialField and various user-specified values from this.state
   * into the latest field specified by the user.
   */
  private getFieldWithChanges(state: FieldTableRowState = this.state): SchemaFieldConstants.SchemaField {
    // TODO(BNCH-93663): Make the properties set on fieldOptionProperties explicit, extra attributes
    // sent to the backend can cause errors.
    const {
      label,
      icon,
      fieldDefinition,
      disabled,
      metadata,
      ...fieldOptionProperties
    }: Partial<SchemaFieldConstants.SchemaField> & {
      fieldDefinition?: FieldDefinition;
      label?: string | JSX.Element;
      icon?: BenchlingIconName;
      disabled?: boolean;
      metadata?: string | null;
    } = state.fieldOption ?? {};

    if (fieldDefinition) {
      if (fieldDefinition === ComputedFieldType.SNAPSHOT) {
        fieldOptionProperties.isSnapshot = true;
        fieldOptionProperties.fieldType = undefined;
      } else {
        fieldOptionProperties.isSnapshot = false;
        fieldOptionProperties.fieldType = fieldDefinition;
      }
    }

    if (!isEqual(state.editingLookupConfig, this.props.initialField.lookupConfig)) {
      fieldOptionProperties.lookupConfig = state.editingLookupConfig;
    }

    if (state.editingUnitData.unitApiIdentifier !== this.props.initialField.unitApiIdentifier) {
      fieldOptionProperties.unitApiIdentifier = state.editingUnitData.unitApiIdentifier;
    }

    if (state.editingDecimalPrecision !== this.props.initialField.decimalPrecision) {
      fieldOptionProperties.decimalPrecision = state.editingDecimalPrecision;
    }

    if (this.props.itemType && !isValidationRulesSupportedType(this.props.itemType)) {
      fieldOptionProperties.simpleRules = [];
    } else if (!isEqual(state.editingSimpleRules.simpleRules, this.props.initialField.simpleRules)) {
      fieldOptionProperties.simpleRules = state.editingSimpleRules.simpleRules;
    }

    const fieldWithChanges = {
      ...this.props.initialField,
      ...fieldOptionProperties,
      name: state.editingName,
      displayName: state.editingName,
      systemName: state.editingSystemName,
      isRequired: state.editingIsRequired,
      isIntegration: state.editingIsIntegration,
      isMulti: state.editingIsMulti,
      isParentLink: state.editingIsParentLink,
      tooltipText: state.editingTooltipText,
    };

    // TODO(BNCH-123622): Remove this when VALIDATION_RULES_ENGINE_ENABLE_M3 is enabled everywhere/removed
    if (!fieldWithChanges.isMulti && fieldWithChanges.simpleRules != null) {
      fieldWithChanges.simpleRules = fieldWithChanges.simpleRules?.filter(
        (rule) => !rule.constantInputs.hasOwnProperty('number_longest_components')
      );
    }

    return fieldWithChanges;
  }

  private submitFieldChange = (): void => {
    this.props.requestChange(this.props.id, this.getFieldWithChanges());
    this.setState({isInEditMode: false, fieldOption: null});
  };

  private changeRequiresFieldMigration(): boolean {
    // if state.fieldOption is null, no change occured to the field definition
    let hasFieldTypeChanged = false;
    if (this.state.fieldOption != null) {
      const fromFieldType = getFieldDefinition(this.props.initialField);
      const toFieldType = this.state.fieldOption.fieldDefinition;

      // coerce null and undefined to the same value of null since fieldOption.schemaFieldSelectorId
      // is undefined when not a selector type and initialField.schemaFieldSelectorId is null
      const oldSchemaFieldSelectorId = this.props.initialField.schemaFieldSelectorId || null;
      const newSchemaFieldSelectorId = this.state.fieldOption.schemaFieldSelectorId || null;
      const selectorChanged = newSchemaFieldSelectorId !== oldSchemaFieldSelectorId;

      const fromRequiredLink = SchemaFieldUtil.simplifyRequiredLink(this.props.initialField.requiredLink);
      const toRequiredLink = SchemaFieldUtil.simplifyRequiredLink(this.state.fieldOption.requiredLink);
      const linkChanged = !isEqual(fromRequiredLink, toRequiredLink);

      hasFieldTypeChanged = fromFieldType !== toFieldType || selectorChanged || linkChanged;
    }

    const isMultiChanged = this.props.initialField.isMulti !== this.state.editingIsMulti;
    return Boolean(
      this.props.isFieldMigrationAvailable &&
      this.props.requestFieldMigrationCreation &&
      !this.props.isFieldBeingCreated &&
      !this.props.isSchemaOrInterfaceBeingCreated &&
      (hasFieldTypeChanged || isMultiChanged)
    );
  }

  private changeWillCauseFieldMigration(): boolean {
    return (
      this.changeRequiresFieldMigration() && !BenchlingConfig.SCHEMAS_ENABLE_MANUAL_SCHEMA_FIELD_MIGRATION
    );
  }

  handleComplete = async (): Promise<void> => {
    if (!this.state.editingName) {
      notifications.warn('Please provide a non-empty name.');
      return;
    }

    const tooltipTextError = getTooltipTextError(this.state.editingTooltipText || '');
    if (tooltipTextError) {
      notifications.warn(tooltipTextError);
      return;
    }

    const isMultiChanged = this.props.initialField.isMulti !== this.state.editingIsMulti;
    const fromFieldDefinition = getFieldDefinition(this.props.initialField);
    const toFieldDefinition = this.state.fieldOption?.fieldDefinition;

    // If the field definition changed, show the migration modal.
    if (this.changeWillCauseFieldMigration()) {
      await legacyRunModal(FieldMigrationCreationModal, {
        initialField: this.props.initialField,
        newField: this.getFieldWithChanges(),
        onRequestSubmit: async (settings: FieldMigrationSettings) => {
          await this.props.requestFieldMigrationCreation!(
            settings.field.apiId,
            settings.field,
            settings.locale
          );
          this.resetFieldChanges();
        },
        schemaFieldSelectors: this.props.schemaFieldSelectors,
        availableFieldOptions: this.props.availableFieldOptions,
        shouldShowIsIntegration: this.props.shouldShowIsIntegration,
        shouldShowIsParentLink: this.props.shouldShowIsParentLink,
        isFieldDefinitionEditable: false,
        numItems: this.props.schemaNumItems || null,
      });
      return;
    }

    const shouldShowWarehouseWarning =
      this.props.shouldShowWarehouseWarning &&
      !this.props.isSchemaOrInterfaceBeingCreated &&
      !this.props.isFieldBeingCreated &&
      BenchlingConfig.ENABLE_WAREHOUSE &&
      (BenchlingConfig.ENABLE_USER_CREATED_WAREHOUSE_CREDENTIALS ||
        isFeatureEnabled({feature: InsightsFeature.INSIGHTS_DISCOVER})) &&
      shouldShowFieldChangeWarehouseWarning(isMultiChanged, fromFieldDefinition, toFieldDefinition);
    const shouldShowRelaxationWarning = this.props.shouldShowRelaxationWarning;
    const shouldShowSubmissionWarning =
      (this.props.shouldShowSubmissionWarningOnCreate || !this.props.isFieldBeingCreated) &&
      this.props.submissionWarnings;
    const modalContents: Array<JSX.Element> = [];
    if (shouldShowSubmissionWarning) {
      modalContents.push(...this.props.submissionWarnings);
    }
    if (shouldShowWarehouseWarning) {
      modalContents.push(
        <FieldChangeWarehouseWarning
          key="warehouse"
          fromFieldDefinition={fromFieldDefinition}
          toFieldDefinition={toFieldDefinition}
          isMultiMoreStrict={!this.state.editingIsMulti && isMultiChanged}
        />
      );
    }
    if (shouldShowRelaxationWarning && this.state.fieldOption) {
      let fromName: string = '';
      if (
        isRequiredLinkTagSchema(this.props.initialField.requiredLink) ||
        isRequiredLinkSchemaInterface(this.props.initialField.requiredLink)
      ) {
        fromName = getArchiveAwareDisplayName(
          this.props.initialField.requiredLink.tagSchema ??
            this.props.initialField.requiredLink.schemaInterface ??
            ANY_ENTITY
        );
      } else if (isRequiredLinkFolderItemType(this.props.initialField.requiredLink)) {
        fromName = typeToName(this.props.initialField.requiredLink.folderItemType);
      } else {
        count('schema.field_table_row.relaxation.missing_from_name');
      }
      let toName: string = ANY_ENTITY.name;
      if (
        isRequiredLinkTagSchema(this.state.fieldOption.requiredLink) ||
        isRequiredLinkSchemaInterface(this.state.fieldOption.requiredLink)
      ) {
        toName = getArchiveAwareDisplayName(
          this.state.fieldOption.requiredLink.tagSchema ??
            this.state.fieldOption.requiredLink.schemaInterface ??
            ANY_ENTITY
        );
      } else {
        count('schema.field_table_row.relaxation.to_any_entity');
      }
      modalContents.push(<FieldRelaxationWarning key="relaxation" fromName={fromName} toName={toName} />);
    }
    if (modalContents.length > 0) {
      await legacyRunModal(FieldChangeWarningModal, {
        onRequestSubmit: () => {
          this.submitFieldChange();
        },
        content: <>{modalContents}</>,
      });
    } else {
      this.submitFieldChange();
    }
  };

  resetFieldChanges = (): void => {
    this.setState({
      isInEditMode: false,
      editingName: this.props.initialField.name,
      editingSystemName: this.props.initialField.systemName,
      editingIsMulti: this.props.initialField.isMulti,
      editingIsParentLink: this.props.initialField.isParentLink || false,
      editingIsRequired: this.props.initialField.isRequired,
      editingIsIntegration: this.props.initialField.isIntegration,
      editingLookupConfig: this.props.initialField.lookupConfig,
      fieldOption: null,
    });
  };

  handleCancel = (): void => {
    if (this.props.initialField.id == null) {
      this.props.requestRemove(this.props.id);
    } else {
      if (this.props.onCancel != null) {
        this.props.onCancel(this.props.id);
      }
      this.resetFieldChanges();
    }
  };

  handleEdit = (): void => {
    if (this.props.onEdit != null) {
      this.props.onEdit(this.props.id);
    }
    this.setState({isInEditMode: true});
  };

  private handleArchive = async (): Promise<void> => {
    if (this.props.initialField.isSnapshot) {
      await Promise.resolve(
        modals.confirm('Archive snapshot field', ARCHIVE_SNAPSHOT_FIELD_WARNING, ButtonStyle.NEGATIVE, {
          acceptText: 'Archive',
          rejectText: 'Cancel',
          enableRedesign: true,
          promiseCompatible: true,
          onSubmit: (): void => {
            this.props.requestArchive!(this.props.id);
          },
        })
      );
    } else {
      this.props.requestArchive!(this.props.id);
    }
  };

  handleRemove = (): void => {
    this.props.requestRemove(this.props.id);
  };

  private handleUnarchive = (): void => {
    this.props.requestUnarchive!(this.props.id);
  };

  private handleKeyDown = (e: ElementKeyboardEvent): void => {
    if (this.props.isSchemaOrInterfaceBeingCreated) {
      return;
    }
    if (keyboard.isEnterKey(e)) {
      e.preventDefault();
      this.handleComplete();
    } else if (keyboard.isEscapeKey(e)) {
      e.preventDefault();
      this.handleCancel();
    }
  };

  override render(): JSX.Element {
    const {
      disabled,
      isDragging,
      disableDrag,
      areRemoveAndArchiveEnabled,
      isSchemaOrInterfaceBeingCreated,
      onDragStart,
      shouldShowIsParentLink,
      shouldShowIsRequired,
      shouldShowIsIntegration,
      shouldShowValidations,
      migratingToFieldName,
      migratingFromFieldName,
      fieldMigrationReviewProps,
    } = this.props;
    const field = this.getFieldWithChanges();
    const disabledOrArchived = disabled || isSchemaFieldArchived(field);
    const isEditingFieldWithUserDefinedType =
      this.state.isInEditMode && !SchemaFieldUtil.isUneditableField(field);
    const isEditable = this.state.isInEditMode && !field.isComputed && !field.isConvertedFromLink;

    const canRemove =
      isSchemaOrInterfaceBeingCreated ||
      (!disabled && areRemoveAndArchiveEnabled && this.props.isRemoveOnly) ||
      (isSchemaFieldArchived(field) && field.isComputed);
    const canArchive =
      !disabledOrArchived &&
      areRemoveAndArchiveEnabled &&
      !isSchemaOrInterfaceBeingCreated &&
      !this.props.isRemoveOnly;
    const canEditExistingField = !disabledOrArchived && !isSchemaOrInterfaceBeingCreated;
    // Ideal if this checked field.permissions.unarchivable, but {permissions} is not currently available in
    // the SchemaField dict. unarchivable is same as writable, so the user can unarchive the field if it's
    // currently archived and the FieldTableRow is not disabled.
    const canUnarchive = !disabled && isSchemaFieldArchived(field);

    const disableMultiSelect = isMultiSelectDisabled(
      field,
      this.props.schemaIsMixtureSchema,
      this.props.disableControlsWithTooltips
    );

    const extraActionButtons = this.props.extraActionButtons
      ? {
          viewMode: [...this.props.extraActionButtons.viewMode],
          editMode: [...this.props.extraActionButtons.editMode],
        }
      : {viewMode: [], editMode: []};
    if (this.props.advancedSettingsComponentForFieldCreation) {
      const field = this.getFieldWithChanges();
      const onUpdateField = (
        update: Pick<
          SchemaField,
          'decimalPrecision' | 'isUneditable' | 'unitApiIdentifier' | 'unitSymbol' | 'simpleRules'
        > & {isRequired?: boolean; isMulti?: boolean}
      ): void => {
        // Check if field was specified in the update to avoid overwriting existing values with null
        const stateUpdate: Partial<FieldTableRowState> = {};
        // eslint-disable-next-line benchlint/no-in-operator
        if ('unitApiIdentifier' in update) {
          stateUpdate.editingUnitData = {
            unitApiIdentifier: update.unitApiIdentifier,
            unitSymbol: update.unitSymbol,
          };
        }
        if (update.simpleRules !== undefined) {
          stateUpdate.editingSimpleRules = {
            simpleRules: update.simpleRules || [],
          };
        }
        if (update.isRequired !== undefined) {
          stateUpdate.editingIsRequired = update.isRequired;
        }
        if (update.isMulti !== undefined) {
          stateUpdate.editingIsMulti = update.isMulti;
        }
        if (update.decimalPrecision !== undefined) {
          stateUpdate.editingDecimalPrecision = update.decimalPrecision;
        }
        // TODO BNCH-87604: Support other options during field creation
        this.setStateAndMaybeUpdateParent(stateUpdate);
      };

      const advancedSettingsIconProps: AdvancedSettingsIconProps = {
        onUpdateField,
        field,
        forFieldCreation: true,
        schemaFieldSelectors: this.props.schemaFieldSelectors,
        disableMultiSelect,
        itemType: this.props.itemType,
      };

      if (this.props.isSchemaOrInterfaceBeingCreated) {
        extraActionButtons.viewMode.push(
          <this.props.advancedSettingsComponentForFieldCreation
            {...advancedSettingsIconProps}
            key="advanced-settings"
          />
        );
      }
      if (this.props.isFieldBeingCreated) {
        extraActionButtons.editMode.push(
          <this.props.advancedSettingsComponentForFieldCreation
            {...advancedSettingsIconProps}
            key="advanced-settings"
          />
        );
      }
    }

    const shouldRenderActionsInBottomRow =
      this.props.compactMode && this.state.isInEditMode && !disabled && !isSchemaOrInterfaceBeingCreated;

    const actionButtonsComponent = !disabled && (
      <FieldActionButtons
        isCompact={shouldRenderActionsInBottomRow}
        isEditing={this.state.isInEditMode && !isSchemaOrInterfaceBeingCreated}
        cancel={this.handleCancel}
        complete={this.handleComplete}
        copyAPIID={
          this.props.initialField.apiId
            ? () => {
                copyToClipboard(this.props.initialField.apiId, {showToastOnSuccess: true});
              }
            : null
        }
        archive={canArchive ? this.handleArchive : null}
        remove={canRemove ? this.handleRemove : null}
        unarchive={canUnarchive ? this.handleUnarchive : null}
        edit={canEditExistingField ? this.handleEdit : null}
        isCompleteEnabled={
          Boolean(this.state.editingName) && !getTooltipTextError(this.state.editingTooltipText || '')
        }
        extraActionButtons={extraActionButtons}
        disableArchiveButtonWithTooltip={
          this.props.disableControlsWithTooltips?.[DisablableControl.ARCHIVE_BUTTON]
        }
        disableDeleteButtonWithTooltip={
          this.props.disableControlsWithTooltips?.[DisablableControl.DELETE_BUTTON]
        }
      />
    );

    const shouldRenderCompactActionsInline =
      this.props.compactMode &&
      actionButtonsComponent &&
      !shouldRenderActionsInBottomRow &&
      !isSchemaOrInterfaceBeingCreated;

    return (
      <div
        className={classNames('simpleDivTable-rowContainer', {
          'is-dragging': this.props.isDragging,
          [css(styles.archivedField)]: isSchemaFieldArchived(field),
        })}
        {...getTestProps({component: 'FieldTableRow', element: 'root'})}
      >
        <a
          className={classNames('u-primary-pointer simpleDivTable-action simpleDivTable-action--move', {
            faded: !isDragging,
            'u-primary-color': isDragging,
            invisible: disableDrag,
          })}
          onMouseDown={disableDrag ? undefined : onDragStart}
        >
          <BenchlingIcon icon="grip-dots-vertical" />
        </a>
        <div className={classNames('simpleDivTable-multiColumnRow', css(styles.fieldTableMultiColumnRow))}>
          <div
            className={classNames('simpleDivTable-row', {
              'is-beingEdited': this.state.isInEditMode,
              'simpleDivTable-row--compact': this.props.compactMode,
            })}
          >
            <div className="simpleDivTable-cell">
              <div className="simpleDivTable-cell-contents">
                {this.state.isInEditMode ? (
                  <TooltipWrapper
                    tooltipTitle={
                      this.props.disableControlsWithTooltips?.[DisablableControl.DISPLAY_NAME_INPUT]
                    }
                  >
                    <FormInput
                      type="text"
                      name="name"
                      value={this.state.editingName}
                      onChange={this.handleChangeName}
                      onKeyDown={this.handleKeyDown}
                      placeholder="Insert a field"
                      disabled={
                        disabledOrArchived ||
                        this.props.disableControlsWithTooltips?.[DisablableControl.DISPLAY_NAME_INPUT] != null
                      }
                      autoFocus={true}
                      dataTestAttrs={{component: 'FieldTableRow', element: 'inputField'}}
                    />
                  </TooltipWrapper>
                ) : (
                  <div className={classNames(css(styles.verticalOrdering))}>
                    <SchemaFieldName
                      field={getSchemaFieldArchivableItem(field)}
                      migratingToFieldName={
                        !fieldMigrationReviewProps?.isReviewableForFieldMigration
                          ? migratingToFieldName
                          : null
                      }
                      migratingFromFieldName={migratingFromFieldName}
                      onFieldNameClick={this.props.fieldNameRenderingProps?.onFieldNameClick}
                      fieldNameTooltip={this.props.fieldNameRenderingProps?.fieldNameTooltip}
                    />

                    {fieldMigrationReviewProps?.isReviewableForFieldMigration && (
                      <Button
                        buttonStyle={ButtonStyle.PRIMARY}
                        size={ButtonSize.MINI}
                        className={css(styles.reviewAndPublishButton)}
                        textClassName="button-text--nowrap"
                        text="Review & Publish"
                        onClick={() => {
                          fieldMigrationReviewProps.reviewMigration();
                        }}
                      />
                    )}
                  </div>
                )}
              </div>
            </div>
            {this.props.shouldShowWarehouseName !== false && (
              <SchemaFieldWarehouseNameField
                editingName={this.state.editingSystemName}
                field={field}
                isBeingEdited={this.state.isInEditMode}
                isDisabled={
                  disabledOrArchived ||
                  this.props.disableControlsWithTooltips?.[DisablableControl.WAREHOUSE_NAME_INPUT] != null
                }
                onChange={this.handleChangeSystemName}
                onKeyDown={this.handleKeyDown}
                formInputTooltip={
                  this.props.disableControlsWithTooltips?.[DisablableControl.WAREHOUSE_NAME_INPUT]
                }
              />
            )}
            {shouldShowIsRequired && (
              <div className="simpleDivTable-cell simpleDivTable-cell--narrow">
                <div
                  className="simpleDivTable-cell-contents--centered"
                  {...getTestProps({component: 'FieldTableRow', element: 'isRequiredCheckboxRoot'})}
                >
                  {(() => {
                    if (isEditingFieldWithUserDefinedType) {
                      return (
                        <TooltipWrapper
                          tooltipTitle={
                            this.props.disableControlsWithTooltips?.[DisablableControl.IS_REQUIRED_CHECKBOX]
                          }
                        >
                          <div
                            {...getTestProps({
                              component: 'FieldTableRow',
                              element: 'isRequiredCheckboxTooltipTrigger',
                            })}
                          >
                            <input
                              type="checkbox"
                              onChange={this.handleChangeIsRequired}
                              checked={this.state.editingIsRequired}
                              name="isRequired"
                              disabled={
                                this.props.disableControlsWithTooltips?.[
                                  DisablableControl.IS_REQUIRED_CHECKBOX
                                ] != null
                              }
                              {...getTestProps({component: 'FieldTableRow', element: 'isRequiredCheckbox'})}
                            />
                          </div>
                        </TooltipWrapper>
                      );
                    } else {
                      return field.isRequired ? '\u2713' : null;
                    }
                  })()}
                </div>
              </div>
            )}
            {shouldShowIsIntegration && (
              <div className="simpleDivTable-cell simpleDivTable-cell--narrow">
                <div
                  className="simpleDivTable-cell-contents--centered"
                  {...getTestProps({component: 'FieldTableRow', element: 'isIntegrationCell'})}
                >
                  {(() => {
                    if (isEditingFieldWithUserDefinedType) {
                      return (
                        <input
                          type="checkbox"
                          onChange={this.handleChangeIsIntegration}
                          checked={this.state.editingIsIntegration}
                          name="isIntegration"
                          {...getTestProps({component: 'FieldTableRow', element: 'isIntegrationCheckbox'})}
                        />
                      );
                    } else {
                      return field.isIntegration ? '\u2713' : null;
                    }
                  })()}
                </div>
              </div>
            )}
            {this.props.shouldShowMultiSelect && (
              <div className="simpleDivTable-cell simpleDivTable-cell--narrow">
                <div
                  className="simpleDivTable-cell-contents--centered"
                  {...getTestProps({component: 'FieldTableRow', element: 'isMultiCheckboxRoot'})}
                >
                  {(() => {
                    if (isEditingFieldWithUserDefinedType) {
                      return (
                        <TooltipWrapper
                          tooltipTitle={
                            this.props.disableControlsWithTooltips?.[DisablableControl.IS_MULTI_CHECKBOX]
                          }
                        >
                          <div>
                            <input
                              type="checkbox"
                              onChange={this.handleChangeIsMulti}
                              checked={this.state.editingIsMulti}
                              disabled={disableMultiSelect}
                              name="isMulti"
                              {...getTestProps({component: 'FieldTableRow', element: 'isMultiCheckbox'})}
                            />
                          </div>
                        </TooltipWrapper>
                      );
                    } else {
                      return field.isMulti ? '\u2713' : null;
                    }
                  })()}
                </div>
              </div>
            )}
            {/* TODO(BNCH-11113) Extract parent link awareness to FileFieldTableRow level */}
            {shouldShowIsParentLink && (
              <div className="simpleDivTable-cell simpleDivTable-cell--narrow">
                <div
                  className="simpleDivTable-cell-contents--centered"
                  {...getTestProps({component: 'FieldTableRow', element: 'isParentLinkCell'})}
                >
                  {(() => {
                    if (isEditingFieldWithUserDefinedType) {
                      const parentLinkDisabledReason = getParentLinkDisabledReason(
                        field,
                        this.props.schemaIsMixtureSchema
                      );
                      return (
                        <TooltipWrapper tooltipTitle={parentLinkDisabledReason}>
                          <div>
                            <input
                              type="checkbox"
                              onChange={this.handleChangeIsParentLink}
                              checked={this.state.editingIsParentLink}
                              disabled={parentLinkDisabledReason != null}
                              name="isParentLink"
                              {...getTestProps({
                                component: 'FieldTableRow',
                                element: 'isParentLinkCheckbox',
                              })}
                            />
                          </div>
                        </TooltipWrapper>
                      );
                    } else {
                      return field.isParentLink ? '\u2713' : null;
                    }
                  })()}
                </div>
              </div>
            )}
            {(() => {
              const isDefinitionLastColumn = !shouldShowValidations && !disabled;
              const isValidationLastColumn = shouldShowValidations && !disabled;

              return (
                <>
                  <FieldDefinitionInputOrDisplay
                    isFieldBeingCreated={this.props.isFieldBeingCreated}
                    availableFieldOptions={this.props.availableFieldOptions}
                    schemaFieldSelectors={this.props.schemaFieldSelectors}
                    batchLinkProps={this.props.batchLinkProps}
                    currentFieldState={{
                      ...this.getFieldWithChanges(),
                      unitSymbol: this.state.editingUnitData.unitSymbol,
                    }}
                    disableFieldDefinitionDropdownTooltip={
                      this.props.disableControlsWithTooltips?.[DisablableControl.FIELD_DEFINITION_DROPDOWN]
                    }
                    inEditMode={isEditable}
                    onSelectFieldOption={(fieldOption) => {
                      this.handleChangeFieldOption(fieldOption);
                    }}
                    ownerUrlId={this.props.ownerUrlId}
                    registryId={this.props.registryId}
                    lookupConfigDescriptionProps={
                      this.state.editingLookupConfig && this.props.lookupConfigModalProps
                        ? {
                            baseSchema: this.props.lookupConfigModalProps.baseSchema ?? null,
                            lookupConfig: this.state.editingLookupConfig,
                            context: this.props.lookupConfigModalProps.context,
                            schemaFieldFilterFn: this.props.lookupConfigModalProps.schemaFieldFilterFn,
                          }
                        : undefined
                    }
                    isLastContentColumn={isDefinitionLastColumn}
                    actionButtons={
                      shouldRenderCompactActionsInline && isDefinitionLastColumn
                        ? actionButtonsComponent
                        : undefined
                    }
                    isCompactMode={this.props.compactMode}
                  />
                  {shouldShowValidations &&
                    (() => {
                      const validations: Array<string> = [];
                      if (field.isRequired) {
                        validations.push('Required');
                      }
                      if (field.isMulti) {
                        validations.push('Multi-select');
                      }
                      field.simpleRules?.forEach((simpleRule: SimpleRule) => {
                        if (
                          validationRuleTemplateIdToDisplayName.hasOwnProperty(
                            simpleRule.validationRuleTemplateId
                          )
                        ) {
                          validations.push(
                            validationRuleTemplateIdToDisplayName[simpleRule.validationRuleTemplateId]
                          );
                        }
                      });
                      field.advancedRules?.forEach((advancedRule: ValidationRule) =>
                        validations.push(advancedRule.name)
                      );

                      let validationContent = null;
                      if (validations.length > 0) {
                        const tooltipText = (
                          <ul className={css(styles.validationsTooltip)}>
                            {validations.map((validation: string) => (
                              <li key={validation}>{validation}</li>
                            ))}
                          </ul>
                        );
                        let cellText;
                        if (validations.length < 3) {
                          cellText = validations.join(', ');
                        } else {
                          cellText = `${validations[0]} and ${validations.length - 1} more`;
                        }
                        validationContent = (
                          <Tooltip text={tooltipText} placement={FloatingPlacement.TOP_START}>
                            <div
                              className={css(styles.validationNames)}
                              {...getTestProps({
                                component: 'FieldTableRow',
                                element: 'validationsTooltipTrigger',
                              })}
                            >
                              {cellText}
                            </div>
                          </Tooltip>
                        );
                      }

                      if (shouldRenderCompactActionsInline && isValidationLastColumn) {
                        return (
                          <SimpleDivTableLastCell actionButtons={actionButtonsComponent}>
                            <div {...getTestProps({component: 'FieldTableRow', element: 'validations'})}>
                              {validationContent}
                            </div>
                          </SimpleDivTableLastCell>
                        );
                      }

                      return (
                        <div className="simpleDivTable-cell">
                          <div
                            className={
                              disabled ? 'simpleDivTable-cell-contents' : 'simpleDivTable-cell-contents--last'
                            }
                            {...getTestProps({component: 'FieldTableRow', element: 'validations'})}
                          >
                            {validationContent}
                          </div>
                        </div>
                      );
                    })()}
                  {!shouldRenderCompactActionsInline &&
                    !shouldRenderActionsInBottomRow &&
                    actionButtonsComponent && (
                      <div className="simpleDivTable-actionGroupContainer">{actionButtonsComponent}</div>
                    )}
                </>
              );
            })()}
            {/* Note: This is not gated on SCHEMAS_ENABLE_UNEDITABLE_FIELDS_CONFIGURATION: if a field is
                configured to be uneditable it will enforce uneditability, even when the config is false.
                The icon hides on hover (unless disabled) so that action buttons can show. */}
            {this.props.isUneditable && (
              <div
                className={classNames('simpleDivTable-actionGroupContainer', {
                  'simpleDivTable-actionGroupContainer--hideOnHover': !disabled,
                })}
              >
                <UneditableFieldIcon iconClassName={css(styles.iconStyle)} />
              </div>
            )}
          </div>
          {this.props.initialField.isComputed &&
            BenchlingConfig.SCHEMAS_ENABLE_COMPUTED_FIELDS_CONFIGURATION_DISPLAY &&
            this.props.schemaId &&
            field.apiId && (
              <div
                className={classNames(
                  css(styles.computedFieldTableRowRow, this.state.isInEditMode && styles.highlightedRow)
                )}
              >
                <Accordion header={<span>&nbsp;&nbsp;Computed Field Description</span>} headerSpacing="none">
                  <QueryingComputedFieldInfo schemaApiId={this.props.schemaId} fieldApiId={field.apiId} />
                </Accordion>
              </div>
            )}
          {this.props.shouldShowTooltipText && (this.state.isInEditMode || field.tooltipText) && (
            <div
              className={classNames(
                css(styles.fieldTableRowRow, this.state.isInEditMode && styles.highlightedRow),
                {'simpleDivTable-row--compact': this.props.compactMode}
              )}
            >
              <div className={classNames('simpleDivTable-cell', css(styles.fieldTableRowTooltipTextCell))}>
                <div className="simpleDivTable-cell-contents">
                  <SchemaFieldTooltipTextField
                    editingTooltipText={this.state.editingTooltipText || ''}
                    field={field}
                    isEditingOrCreating={this.state.isInEditMode}
                    isDisabled={disabledOrArchived}
                    onChange={this.handleChangeTooltipText}
                    onKeyDown={this.handleKeyDown}
                  />
                </div>
              </div>
            </div>
          )}
          {shouldRenderActionsInBottomRow && (
            <div
              className={classNames('is-beingEdited', css(styles.highlightedRow, styles.editModeActionRow))}
            >
              <div className={css(styles.editModeActionCell)}>{actionButtonsComponent}</div>
            </div>
          )}
          {this.changeWillCauseFieldMigration() && (
            <div
              className={classNames(
                css(styles.fieldTableRowRow, this.state.isInEditMode && styles.highlightedRow)
              )}
            >
              <div className={classNames('simpleDivTable-cell', css(styles.fieldTableMigrationWarningRow))}>
                <div className="simpleDivTable-cell-contents">
                  <InlineBanner
                    bannerType={InlineBannerType.WARNING}
                    text="This change will require a field migration"
                    supportingInfo="To avoid data loss, your existing field will be archived and the data preserved. Data will be copied to your new field where possible. No other changes to this field can be made during the migration."
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
}

export default FieldTableRow;

const styles = StyleSheet.create({
  fieldTableMultiColumnRow: {
    display: 'flex',
    flexDirection: 'column',
    marginRight: '-25px',
    width: '100%',
    ':hover': {
      backgroundColor: GRAY_LIGHTER,
    },
  },
  fieldTableRowTooltipTextCell: {
    flex: 'auto',
    width: '100%',
  },
  fieldTableRowRow: {
    display: 'flex',
    flexDirection: 'row',
    marginRight: '-25px',
    width: '100%',
    padding: '0px 14px 10px 14px',
  },
  computedFieldTableRowRow: {
    display: 'flex',
    flexDirection: 'row',
    marginRight: '-25px',
    width: '100%',
    padding: '0px 14px 10px 14px',
  },
  highlightedRow: {
    backgroundColor: GRAY_LIGHTER,
  },
  archivedField: {
    opacity: 0.6,
  },
  iconStyle: {
    color: GRAY_DARKER,
    marginRight: '10px',
    height: '12px',
    width: '12px',
  },
  verticalOrdering: {
    display: 'flex',
    flexDirection: 'column',
  },
  reviewAndPublishButton: {
    width: '120px',
  },
  fieldTableMigrationWarningRow: {
    flex: 'auto',
    width: '100%',
    lineHeight: '20px',
    fontSize: '13px',
  },
  customList: {
    listStyleType: 'none',
    paddingLeft: '15px',
  },
  listItem: {
    position: 'relative',
    paddingLeft: '20px',
    '::before': {
      content: '"\\2043"',
      color: Colors.GRAY_DARK,
      position: 'absolute',
      left: '0',
      fontSize: '30px',
      top: '-2px',
    },
  },
  dependencyItem: {
    position: 'relative',
    paddingLeft: '15px',
    '::before': {
      content: '"\\2043"',
      color: Colors.GRAY_DARK,
      position: 'absolute',
      left: '0',
      fontSize: '20px',
    },
  },
  validationsTooltip: {
    paddingLeft: '18px',
    marginBottom: '3px',
  },
  validationNames: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  editModeActionRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    padding: '10px 14px',
  },
  editModeActionCell: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
});
